/* Per-screen help content.
 *
 * Content lives here as plain data, not inside the screens, so that:
 *   - HelpDialog stays a dumb renderer,
 *   - a screen only needs one line to gain help,
 *   - the wording can be reviewed in one place rather than hunted across files.
 *
 * Shape:
 *   {
 *     title:    dialog heading
 *     subtitle: one line under it
 *     sections: [{ id, label, icon?, blocks: [...] }]
 *   }
 *
 * A section's `blocks` are rendered in order by HelpDialog. Supported blocks:
 *   { type: "text",   text }
 *   { type: "list",   items: [string] }                 bulleted
 *   { type: "steps",  items: [string] }                 numbered
 *   { type: "fields", items: [{ term, desc }] }         label / explanation pairs
 *   { type: "note",   tone: "info"|"warn", text }       callout
 *
 * Keep it accurate. Help that describes behaviour the screen does not have is
 * worse than no help, because it sends people looking for buttons that aren't
 * there.
 */

const ticketsHelp = {
  title: "Jira Work Items",
  subtitle:
    "Browse the work items your team has put into Testing, and turn each one into tests, runs and reports.",
  sections: [
    {
      id: "what",
      label: "What this screen is",
      icon: "Book",
      blocks: [
        {
          type: "text",
          text:
            "This is the starting point for testing a piece of work. It reads your connected Jira project and lists the work items that are ready to be tested, so you can go from a ticket to test cases, to a run, to a report without leaving the screen.",
        },
        {
          type: "text",
          text:
            "It shows only work items whose Jira status is Testing. That is deliberate — the list is meant to be a to-do queue for QA, not a browser for the whole backlog. If a ticket you expect is missing, check its status in Jira first.",
        },
        {
          type: "fields",
          items: [
            {
              term: "Connection banner",
              desc:
                "Green means CortexQA reached Jira and shows which site and account it used. Red means the connection failed, and nothing below it will load until that is fixed.",
            },
            {
              term: "Project and Epic",
              desc:
                "Pick a project, then an epic. Work items load only once an epic is chosen, which keeps large projects from loading thousands of rows.",
            },
            {
              term: "Refresh",
              desc:
                "Re-checks the Jira connection, reloads the work items and re-reads what is already linked to them.",
            },
          ],
        },
      ],
    },
    {
      id: "getting-started",
      label: "Getting started",
      icon: "Play",
      blocks: [
        {
          type: "steps",
          items: [
            "Confirm the banner at the top is green. If it is red, the Jira credentials need attention before anything else will work.",
            "Choose a project from the first dropdown.",
            "Choose an epic. The work items in that epic with status Testing are loaded.",
            "Use the search box to narrow the list by key or summary.",
            "Click a work item to open its detail view, where its tests, runs and reports live.",
          ],
        },
        {
          type: "note",
          tone: "info",
          text:
            "Nothing on this screen changes your Jira data until you explicitly create something. Browsing, searching and opening a work item are all read-only.",
        },
      ],
    },
    {
      id: "detail",
      label: "The work item detail view",
      icon: "List",
      blocks: [
        {
          type: "text",
          text:
            "Opening a work item shows everything CortexQA knows about it in one place: which test cases are attached, which executions have been created from it, and which reports exist.",
        },
        {
          type: "fields",
          items: [
            {
              term: "Add tests",
              desc:
                "Two choices. Create tests opens the authoring flow to build new test cases for this work item. Add existing tests links test cases you already have on a test suite.",
            },
            {
              term: "Create execution",
              desc:
                "Assembles a test cycle from this work item's automated tests. See the Creating an execution section — this one has a precondition.",
            },
            {
              term: "Reports",
              desc:
                "Appears against an execution once it has started or completed. The eye icon opens the report, the pencil changes which report types it contains.",
            },
          ],
        },
        {
          type: "note",
          tone: "info",
          text:
            "Use the back arrow at the top to return to the list. Your project, epic and search are kept, so you do not have to set the filters up again.",
        },
      ],
    },
    {
      id: "creating-tests",
      label: "Creating tests from a work item",
      icon: "Plus",
      blocks: [
        {
          type: "text",
          text:
            "Create tests asks two things before it starts: where the tests should live, and how they should be written.",
        },
        {
          type: "fields",
          items: [
            {
              term: "Where they go",
              desc:
                "A test suite and, optionally, a category. CortexQA tries to resolve this from the work item's own Category field so you usually do not have to choose.",
            },
            {
              term: "What already exists",
              desc:
                "Before you author anything, the dialog lists the tests already linked to this work item and which of them already have an automated test case. This is here so you do not rebuild work that exists.",
            },
            {
              term: "AI or Manual",
              desc:
                "AI opens an agent that drives a browser and drafts the steps for you. Manual opens a recorder where you click through the screen yourself and the steps are captured.",
            },
          ],
        },
        {
          type: "note",
          tone: "warn",
          text:
            "Read the What already exists table before you continue. Creating a second test for something already covered is the most common way this screen gets misused.",
        },
      ],
    },
    {
      id: "creating-execution",
      label: "Creating an execution",
      icon: "Play",
      blocks: [
        {
          type: "text",
          text:
            "An execution is a test cycle: a set of test cases run together, with evidence captured per step. Creating one from a work item pre-selects that work item's automated tests.",
        },
        {
          type: "text",
          text:
            "Before the wizard opens, CortexQA checks whether the work item can actually be executed and reported on. It needs a complete chain: the work item is linked to a test, and that test has an Artifact built from it. Only that chain lets results be pushed back to Jira step by step.",
        },
        {
          type: "list",
          items: [
            "No tests linked — author the test design first, then automate it.",
            "Tests exist but none are automated — use Create tests on this work item.",
            "Jira could not be read — a connection or permission problem, not a data problem.",
          ],
        },
        {
          type: "note",
          tone: "warn",
          text:
            "If the check refuses, it tells you which of those three it is. That message is the thing to act on — it names exactly what is missing.",
        },
      ],
    },
    {
      id: "reports",
      label: "Reports",
      icon: "Chart",
      blocks: [
        {
          type: "text",
          text:
            "Report actions appear on an execution once it is in progress or completed. There is nothing to report on before a run has started.",
        },
        {
          type: "fields",
          items: [
            {
              term: "Eye icon",
              desc:
                "Opens the report. The first time, you choose which report types to include; after that it opens straight away using the types you picked.",
            },
            {
              term: "Pencil icon",
              desc: "Changes which report types the execution's report contains.",
            },
          ],
        },
      ],
    },
    {
      id: "troubleshooting",
      label: "If something looks wrong",
      icon: "Alert",
      blocks: [
        {
          type: "fields",
          items: [
            {
              term: "Not connected to Jira",
              desc:
                "The site URL, account email or API token is missing or rejected. Nothing on this screen will load until the connection is green.",
            },
            {
              term: "The epic list is empty",
              desc:
                "The account can see the project but it has no epics, or no permission to read them. Confirm in Jira with the same account.",
            },
            {
              term: "A work item is missing",
              desc:
                "This screen lists only items in Testing status. Check the item's status in Jira, and that it sits under the epic you selected.",
            },
            {
              term: "Nothing loads at all",
              desc:
                "Usually the API or its database is not running rather than a Jira problem. Refresh first; if it persists, the server needs attention.",
            },
          ],
        },
        {
          type: "note",
          tone: "info",
          text:
            "Refresh re-reads everything on the screen. It is safe to press at any time and never changes data.",
        },
      ],
    },
  ],
};

/* ================================================================== */
/*  Dashboard                                                          */
/* ================================================================== */

const dashboardHelp = {
  title: "Dashboard",
  subtitle: "A read-only overview of every test suite and test case you have.",
  sections: [
    {
      id: "what",
      label: "What this screen is",
      icon: "Book",
      blocks: [
        {
          type: "text",
          text:
            "The landing page. It totals up every test suite and test case so you can see the health of the whole workspace at a glance, then click straight through to whatever needs attention.",
        },
        {
          type: "text",
          text:
            "Nothing on this screen changes data. Every number is derived from the test cases that already exist, so it is safe to explore.",
        },
      ],
    },
    {
      id: "reading-it",
      label: "Reading the numbers",
      icon: "Chart",
      blocks: [
        {
          type: "fields",
          items: [
            {
              term: "Test Suites",
              desc: "How many test suites exist, with the one holding the most test cases named underneath.",
            },
            {
              term: "Test cases",
              desc: "Every test case across every test suite, with a count of those that have never been run.",
            },
            {
              term: "Pass rate",
              desc:
                "Passed test cases as a percentage of all of them. A case that has never run counts against the rate, so a new test suite starts at 0%.",
            },
            {
              term: "Registered users",
              desc: "How many accounts can sign in. Shows a dash if the users list could not be read.",
            },
            {
              term: "Status distribution",
              desc: "The same totals as a bar: passed, failed, and not run.",
            },
          ],
        },
        {
          type: "note",
          tone: "info",
          text:
            "A test case's status is the outcome of its most recent run. Running, cancelled and never-run all fall into the grey \"Not run\" bucket.",
        },
      ],
    },
    {
      id: "getting-around",
      label: "Getting around from here",
      icon: "List",
      blocks: [
        {
          type: "list",
          items: [
            "Recent test cases lists the six newest cases across all test suites — click one to open its test suite.",
            "Test Suites overview is the full table, one row per test suite, with its own pass-rate bar. Click any row to open that test suite.",
            "View all test suites and Open test suites both jump to the Test Suites list.",
            "Refresh re-reads every test suite's test cases. Useful after a run finishes elsewhere.",
          ],
        },
      ],
    },
    {
      id: "troubleshooting",
      label: "If something looks wrong",
      icon: "Alert",
      blocks: [
        {
          type: "fields",
          items: [
            {
              term: "Every count is zero",
              desc:
                "Either no test suites exist yet, or the API could not be reached. Press Refresh; if a red banner appears, the server needs attention.",
            },
            {
              term: "A test suite shows fewer cases than you expect",
              desc:
                "This screen counts cases per test suite directly. If one test suite failed to load, it is counted as empty rather than dropped — Refresh usually fixes it.",
            },
            {
              term: "Pass rate looks low",
              desc:
                "Check the \"not run yet\" figure first. Unrun cases drag the rate down; they are not failures.",
            },
          ],
        },
      ],
    },
  ],
};

/* ================================================================== */
/*  Boards list                                                        */
/* ================================================================== */

const boardsHelp = {
  title: "Test Suites",
  subtitle: "Every test suite in the product, as cards or a table.",
  sections: [
    {
      id: "what",
      label: "What this screen is",
      icon: "Book",
      blocks: [
        {
          type: "text",
          text:
            "A test suite is a collection of automated test cases covering one area of the product. This screen lists them all and is the way into each one.",
        },
        {
          type: "text",
          text:
            "Each one summarises how many tests it holds and how many passed or failed on their last run, so you can pick the one that needs work without opening it first.",
        },
      ],
    },
    {
      id: "using-it",
      label: "Using this screen",
      icon: "Play",
      blocks: [
        {
          type: "steps",
          items: [
            "Use the search box to narrow the list by test suite name or description.",
            "Switch between Card view and Grid view with the two icons next to Add test suite — same test suites, different density.",
            "Click a test suite (card or row) to open it and work with its test cases.",
            "Use Add test suite to start a new one.",
          ],
        },
      ],
    },
    {
      id: "adding",
      label: "Adding a test suite",
      icon: "Plus",
      blocks: [
        {
          type: "fields",
          items: [
            {
              term: "Test Suite name",
              desc: "Required. Name it after the area under test, for example \"Checkout Regression Suite\".",
            },
            {
              term: "Description",
              desc: "Optional. What the suite covers. Shown under the name in both views.",
            },
          ],
        },
        {
          type: "text",
          text:
            "Create test suite saves it and opens it immediately, ready for its first test case.",
        },
      ],
    },
    {
      id: "deleting",
      label: "Deleting a test suite",
      icon: "Alert",
      blocks: [
        {
          type: "text",
          text:
            "The bin icon appears on a card when you hover it, and sits in the Actions column in Grid view. You will be asked to confirm.",
        },
        {
          type: "note",
          tone: "warn",
          text:
            "Deleting a test suite also deletes its test cases, its categories and all of its run history. This cannot be undone.",
        },
      ],
    },
  ],
};

/* ================================================================== */
/*  Board detail                                                       */
/* ================================================================== */

const boardDetailHelp = {
  title: "Test Suite",
  subtitle: "Create, organise and run the automated test cases in one test suite.",
  sections: [
    {
      id: "what",
      label: "What this screen is",
      icon: "Book",
      blocks: [
        {
          type: "text",
          text:
            "The working surface for one test suite. Everything you do with an automated test case happens here: create it, inspect its steps, run it, watch it run, and review what happened.",
        },
        {
          type: "text",
          text:
            "A test suite has two levels beneath it. Categories split it into areas and appear as cards at the top. Sub-categories bundle cases that belong together so they can be run in sequence.",
        },
      ],
    },
    {
      id: "layout",
      label: "How the page is arranged",
      icon: "List",
      blocks: [
        {
          type: "list",
          items: [
            "At test suite level you see the category cards first, then the sub-categories and loose test cases that sit directly on the test suite.",
            "Click a category card to drill into it. The heading becomes a breadcrumb, and the Categories button takes you back up to the category list.",
            "Each test case is one row. The chevron expands it to show its steps and, after a run, each step's outcome and screenshot.",
            "Search filters by test case name, page or priority. The ticket dropdown narrows the list to cases linked to one Jira work item.",
          ],
        },
        {
          type: "note",
          tone: "info",
          text:
            "Filters only hide rows; they never change data. A sub-category whose members are all filtered out disappears with them.",
        },
      ],
    },
    {
      id: "creating",
      label: "Creating a test case",
      icon: "Plus",
      blocks: [
        {
          type: "text",
          text:
            "Create test case is a split button. The arrow offers two ways to build one, and whichever you pick opens as a full page.",
        },
        {
          type: "fields",
          items: [
            {
              term: "via AI",
              desc:
                "Describe what to test and the agent drives the real application, works out the steps and records them for you.",
            },
            {
              term: "via Manual",
              desc:
                "Build the steps yourself, one at a time. Use this when you know exactly what the test should do.",
            },
          ],
        },
        {
          type: "text",
          text:
            "If you start from inside a category the new case is filed there automatically. Start from test suite level and it lands on the test suite.",
        },
      ],
    },
    {
      id: "running",
      label: "Running a test case",
      icon: "Play",
      blocks: [
        {
          type: "steps",
          items: [
            "Press Run on a row. The case expands and each step turns green or red as it executes.",
            "Pause holds the run between steps and Continue releases it. Stop ends it at the next checkpoint and records the run as cancelled.",
            "If a step needs a value the test did not store, a prompt appears — type it and the run carries on.",
            "Run all on a sub-category header runs every case in it, one after another.",
          ],
        },
        {
          type: "note",
          tone: "info",
          text:
            "Use View on a row for the full-page run view: a live picture of the browser beside the step list. Run history shows previous runs of the same case.",
        },
      ],
    },
    {
      id: "organising",
      label: "Organising the suite",
      icon: "Boards",
      blocks: [
        {
          type: "fields",
          items: [
            {
              term: "Add category",
              desc:
                "Creates an area within the test suite. Only offered at test suite level, since categories do not nest.",
            },
            {
              term: "Select to organise",
              desc:
                "Turns on tick boxes. Choose some cases, then \"Add to a sub-category\" to file them, or \"Remove from sub-category\" to take them out again.",
            },
            {
              term: "Datasource",
              desc:
                "If a case has saved value sets, the arrow beside Run lets you pick one so the run does not stop to ask for values.",
            },
          ],
        },
      ],
    },
    {
      id: "deleting",
      label: "Deleting things",
      icon: "Alert",
      blocks: [
        {
          type: "fields",
          items: [
            {
              term: "Delete a test case",
              desc: "From the row's menu. Asks for confirmation and cannot be undone.",
            },
            {
              term: "Delete a category",
              desc:
                "The bin on a category card. Its test cases are kept — they move out to the test suite rather than being deleted.",
            },
            {
              term: "Delete test suite",
              desc: "Removes the test suite with all of its test cases, categories and run history.",
            },
          ],
        },
        {
          type: "note",
          tone: "warn",
          text: "Deleting a test suite or a test case is permanent. There is no undo and no recycle bin.",
        },
      ],
    },
  ],
};

/* ================================================================== */
/*  Executions                                                         */
/* ================================================================== */

const executionsHelp = {
  title: "Artifacts",
  subtitle: "Pick an epic, then a test, and run it — every run's result goes to Jira.",
  sections: [
    {
      id: "what",
      label: "What this screen is",
      icon: "Book",
      blocks: [
        {
          type: "text",
          text:
            "Left to right: the space's Epics, each opening into its stories as a tree; the chosen story's Artifacts (one card per test); and the chosen Artifact's trial runs — new (saved, not executed yet) first, then every execution. Artifacts filed under no story are under No story.",
        },
        {
          type: "text",
          text:
            "Every execution here is a trial run: it builds and checks a script, runs on the server, writes nothing to Jira and is never evidence. Evidence is recorded from For you › Test Runs. “Save & Execute” on the Requirement Board opens this screen on the new scripts.",
        },
      ],
    },
    {
      id: "tests",
      label: "The tests column",
      icon: "List",
      blocks: [
        {
          type: "fields",
          items: [
            { term: "One per test", desc: "Each test is listed once, even when several Artifacts were saved for it." },
            { term: "Badge", desc: "The colour on the test icon is its latest result: green passed, red failed, blue running or new." },
            { term: "N new", desc: "Scripts saved for this test that have not run yet." },
            { term: "Search", desc: "A story or test key (e.g. 186, CTO-200) or part of its name." },
            { term: "Bug", desc: "The red key under a test is the Jira Bug raised from its last failure." },
          ],
        },
      ],
    },
    {
      id: "run",
      label: "Creating and executing",
      icon: "Play",
      blocks: [
        {
          type: "steps",
          items: [
            "Press Create on a test. Any status but Obsolete can be tried; a Draft, In Review or Needs Update test shows a warning first, because its trial is not evidence.",
            "Create makes the trial execution in CortexQA only. It appears on the right as New.",
            "Press Execute on that card. The script runs on the server — you can leave the page — and its screenshots are kept here. Nothing goes to Jira.",
            "If the Artifact uses variables, Execute first asks for their values. The › button next to each lets you choose any entry of your Settings › Configuration, whatever its name (DEV_URL for {BASE_URL}); its value goes into the field as if typed.",
            "🗑 on a trial run deletes it, with its results and evidence. 🗑 on an Artifact deletes it with its trial runs. Both ask first; tick Delete the Log to delete their Log entries too (left unticked, they stay). The loader says each step as it is done. Every trial run can be deleted. A formal run (For you › Test Runs) and an Artifact that ran in one can't be deleted, and nothing can be deleted while its trial runs.",
            "A finished card offers Run again: each attempt is a new run of the same execution. Click its name to see every step with its screenshots.",
            "The Log on a test or a card shows who created, re-created and ran it, when, and how it ended.",
          ],
        },
      ],
    },
    {
      id: "bug",
      label: "When a run fails — Raise a defect",
      icon: "Alert",
      blocks: [
        {
          type: "text",
          text:
            "A failed run shows Raise a defect (the bug button). Review the pre-filled summary and description, then create it: the Bug is filed under the epic, linked to the story and gets the failing step's screenshots. The trial itself stays out of Jira.",
        },
        {
          type: "note",
          tone: "info",
          text: "After that the run (and the test) show the Bug's key instead of the button.",
        },
      ],
    },
    {
      id: "running",
      label: "Inside a run",
      icon: "Play",
      blocks: [
        {
          type: "text",
          text:
            "Run execution works through the cases one at a time. Each case expands as it runs and every step is captured with a before and an after screenshot — that is the evidence.",
        },
        {
          type: "list",
          items: [
            "Pause holds between steps; Resume releases it.",
            "Stop finishes the case in flight and skips the rest. Cases already run keep their results.",
            "View in the Before or After column opens that screenshot full size.",
            "Add test cases, Link tickets and the result dropdowns are locked while a run is in progress.",
          ],
        },
        {
          type: "note",
          tone: "info",
          text:
            "You can also set a result by hand from the dropdown on a row — passed, failed, blocked, skipped or not run. Useful for a case that was checked outside the tool.",
        },
      ],
    },
    {
      id: "xray",
      label: "Results and Jira",
      icon: "Boards",
      blocks: [
        {
          type: "text",
          text:
            "Nothing on this screen sends results to Jira. A formal run, the evidence, is executed, reviewed and submitted from For you › Test Runs, on an Approved Test.",
        },
        {
          type: "fields",
          items: [
            {
              term: "Recorded in Jira as …",
              desc:
                "An older trial that went to Jira before trial runs stayed local. It is read-only here: it can't be run again or deleted.",
            },
            {
              term: "View Execution Report",
              desc:
                "Reads such an older execution back from Jira, with a link to open it there.",
            },
          ],
        },
      ],
    },
  ],
};

/* ================================================================== */
/*  Datasource                                                         */
/* ================================================================== */

const datasourceHelp = {
  title: "Datasource",
  subtitle: "Saved sets of values that fill a test case's dynamic fields, so a run does not stop to ask.",
  sections: [
    {
      id: "what",
      label: "What this screen is",
      icon: "Book",
      blocks: [
        {
          type: "text",
          text:
            "When a test case has a step marked dynamic, the runner has to get that value from somewhere. Without a datasource it stops mid-run and asks you. A datasource answers those questions in advance.",
        },
        {
          type: "text",
          text:
            "Each datasource belongs to exactly one test case and holds one value per dynamic field. Give the same case several of them — one per scenario — and pick which to use when you run it.",
        },
        {
          type: "note",
          tone: "info",
          text:
            "Only test cases with at least one dynamic step can have a datasource. If the list of test cases is empty, record a case and mark a step Dynamic first.",
        },
      ],
    },
    {
      id: "the-table",
      label: "The table",
      icon: "List",
      blocks: [
        {
          type: "fields",
          items: [
            { term: "Name", desc: "The label you pick when choosing a datasource at run time." },
            { term: "Test case", desc: "The case it belongs to. This cannot be changed after creation." },
            { term: "Keys", desc: "How many values it holds." },
          ],
        },
        {
          type: "text",
          text:
            "Search matches the datasource name or the test case name. The row icons view, edit and delete; the bin at the top removes everything you have ticked. Ten rows per page.",
        },
      ],
    },
    {
      id: "creating",
      label: "Creating one",
      icon: "Plus",
      blocks: [
        {
          type: "steps",
          items: [
            "Press Add Datasource and pick the test case. The bracketed number is how many values it needs.",
            "Give it a name that describes the scenario, for example \"Positive login\".",
            "Fill in the Values panel. Each field is labelled with its variable name and shows the kind of input it drives.",
            "Save.",
          ],
        },
        {
          type: "note",
          tone: "info",
          text:
            "Leave a value blank and the runner will still prompt for that one field. Blank means \"ask me\", not \"empty string\".",
        },
      ],
    },
    {
      id: "json",
      label: "Filling values from a file",
      icon: "Download",
      blocks: [
        {
          type: "fields",
          items: [
            {
              term: "Template",
              desc:
                "Downloads a JSON file with the right keys already in it and blank values, so you know exactly what to fill in.",
            },
            {
              term: "Upload JSON",
              desc:
                "Reads a JSON object and maps its keys onto the fields. Keys the test case does not recognise are ignored and listed.",
            },
          ],
        },
        {
          type: "text",
          text:
            "After an upload a banner reports how many values loaded. An amber warning means a value's type does not match the field it drives — it is kept, but may not apply cleanly at run time.",
        },
      ],
    },
    {
      id: "care",
      label: "Things to watch",
      icon: "Alert",
      blocks: [
        {
          type: "list",
          items: [
            "Changing the chosen test case while adding one rebuilds the fields, dropping values whose keys are not in the new case.",
            "Fields whose kind is element, link or button warn you: the value may not apply reliably.",
            "Deleting is permanent, and the bulk delete covers everything ticked, including rows on other pages.",
          ],
        },
        {
          type: "note",
          tone: "warn",
          text:
            "A password field's value is stored so the run can use it. It is masked while editing, but treat any datasource holding credentials as sensitive.",
        },
      ],
    },
  ],
};

/* ================================================================== */
/*  Reports                                                            */
/* ================================================================== */

const reportsHelp = {
  title: "Results, Reporting & Analytics",
  subtitle: "Generate and sign reports, trace requirements, and analyse results.",
  sections: [
    {
      id: "what",
      label: "What this screen is",
      icon: "Book",
      blocks: [
        {
          type: "text",
          text:
            "Where a finished execution becomes a document you can hand over. A report freezes one execution's results, can be signed electronically, and exports as PDF, JSON or HTML.",
        },
        {
          type: "fields",
          items: [
            { term: "Reports", desc: "Generate, sign, export and delete reports." },
            { term: "Traceability", desc: "Which requirements and tickets are covered by which tests, and where the gaps are." },
            { term: "Analytics", desc: "Trends and breakdowns across results." },
            { term: "History", desc: "Past runs." },
          ],
        },
      ],
    },
    {
      id: "creating",
      label: "Creating a report",
      icon: "Plus",
      blocks: [
        {
          type: "text",
          text: "Create report opens a four-step wizard. You cannot skip ahead of a step that is not yet valid.",
        },
        {
          type: "steps",
          items: [
            "Details — Report name is required. Description, Environment, Build and Executor are optional but appear in the document.",
            "Execution — pick the one execution the report draws from. If this is empty, create an execution first.",
            "Report types — tick one or more. Each adds its own sections.",
            "Review — check the summary and the live PDF preview, then Create report.",
          ],
        },
        {
          type: "fields",
          items: [
            { term: "Test Summary", desc: "Scope, metrics, environment, exit criteria and sign-off." },
            { term: "Detailed Test", desc: "Every step with its before and after evidence screenshots." },
            { term: "Defect", desc: "Failed cases written up as defects, with severity and module." },
            { term: "Test Coverage", desc: "Requirement and ticket to test-case mapping, and the gaps." },
            { term: "Automation", desc: "Automated run results: pass/fail, steps, timestamps." },
            { term: "Progress / Status", desc: "Execution status, counts, and what is left." },
          ],
        },
      ],
    },
    {
      id: "signing",
      label: "Signing a report",
      icon: "Check",
      blocks: [
        {
          type: "text",
          text:
            "Sign records who approved the report and why. Re-enter your user ID and password, and pick a meaning: Approved, Reviewed, Authored or Verified.",
        },
        {
          type: "note",
          tone: "warn",
          text:
            "Signing is a legally binding act and it locks the report. A signed report cannot be edited or deleted — its bin icon is disabled from then on.",
        },
      ],
    },
    {
      id: "exporting",
      label: "Exporting",
      icon: "Download",
      blocks: [
        {
          type: "list",
          items: [
            "Export on a row offers PDF, JSON and HTML, and downloads the file.",
            "Tick several rows and use \"Download selected\" to get one zip of PDFs.",
            "Clear drops the selection without touching anything.",
          ],
        },
        {
          type: "text",
          text:
            "The search box filters by report name, description or test suite. It only appears once at least one report exists.",
        },
      ],
    },
  ],
};

/* ================================================================== */
/*  Scheduling                                                         */
/* ================================================================== */

const schedulingHelp = {
  title: "Test Scheduling",
  subtitle: "Run test cases and suites automatically — one-off, recurring or on a cron expression.",
  sections: [
    {
      id: "what",
      label: "What this screen is",
      icon: "Book",
      blocks: [
        {
          type: "text",
          text:
            "Set tests running without anyone present. A schedule names what to run, when to run it, and who to tell about the outcome. The server does the rest.",
        },
        {
          type: "text",
          text:
            "Each row is one schedule. The table shows what it targets, its cadence and time zone, when it next fires, how it went last time, and whether it is switched on.",
        },
      ],
    },
    {
      id: "creating",
      label: "Creating a schedule",
      icon: "Plus",
      blocks: [
        {
          type: "text",
          text: "New schedule opens a five-step wizard. Edit reuses the same wizard, starting from step one.",
        },
        {
          type: "steps",
          items: [
            "Details — Schedule name is required; Description is optional.",
            "Target — pick a test suite, then either all of its test cases or specific ones from the tree. Tick a sub-category or category to take everything under it.",
            "Schedule — choose Once, Recurring or Cron, set the time zone, then the time, the interval, or the five-field cron expression.",
            "Options — Environment, Build, what to do on overlap, and who to email.",
            "Review — check the summary and save.",
          ],
        },
        {
          type: "fields",
          items: [
            {
              term: "On overlap",
              desc:
                "What happens if the previous run is still going: skip this one, queue it behind, or allow both at once.",
            },
            {
              term: "Notify on",
              desc: "Email every run, or only the ones that fail. Recipients is a comma-separated list.",
            },
            {
              term: "Enabled",
              desc: "Untick to save the schedule without activating it. You can switch it on from the list later.",
            },
          ],
        },
        {
          type: "note",
          tone: "info",
          text:
            "Pick the test suite before the test-case tree will load. Changing the test suite afterwards clears what you had selected.",
        },
      ],
    },
    {
      id: "managing",
      label: "Managing schedules",
      icon: "List",
      blocks: [
        {
          type: "fields",
          items: [
            { term: "Enabled toggle", desc: "Switches the schedule on or off. Off blanks the Next run column." },
            { term: "Run now", desc: "Fires it immediately, whether or not it is enabled." },
            { term: "Run history", desc: "Every time it fired: when, what triggered it, the outcome, and whether email went out." },
            { term: "Audit trail", desc: "Who changed the schedule and when." },
            { term: "Delete", desc: "Removes the schedule after confirming. Its run history goes with it." },
          ],
        },
        {
          type: "note",
          tone: "info",
          text:
            "Run now does not refresh the row, so Last status stays stale until you press Refresh.",
        },
      ],
    },
    {
      id: "timezones",
      label: "Time zones",
      icon: "Clock",
      blocks: [
        {
          type: "text",
          text:
            "Time zone defaults to your browser's and governs when a recurring or cron schedule fires on the server.",
        },
        {
          type: "note",
          tone: "warn",
          text:
            "For a one-off, the Run at time is read in your browser's time zone, not the Time zone you picked above it. If the two differ, set Run at to the wall-clock time on your own machine.",
        },
      ],
    },
  ],
};

/* ================================================================== */
/*  Users                                                              */
/* ================================================================== */

const usersHelp = {
  title: "Users",
  subtitle: "Create, edit and remove the accounts that can sign in to the suite.",
  sections: [
    {
      id: "what",
      label: "What this screen is",
      icon: "Book",
      blocks: [
        {
          type: "text",
          text:
            "The account directory. Every person who signs in has a row here. The table shows their login ID, display name, email, and when the record was created or last changed.",
        },
        {
          type: "text",
          text:
            "Search matches ID, name or email. Ten rows per page. The row icons view, edit and delete; the bin at the top removes everything you have ticked.",
        },
      ],
    },
    {
      id: "adding",
      label: "Adding a user",
      icon: "Plus",
      blocks: [
        {
          type: "fields",
          items: [
            {
              term: "User ID",
              desc: "Required. What they type to sign in. It cannot be changed afterwards, so choose carefully.",
            },
            { term: "Display Name", desc: "Required. How they appear throughout the app." },
            { term: "Email", desc: "Required, and must be a valid address." },
            { term: "Password", desc: "Required. At least 8 characters." },
            { term: "Confirm Password", desc: "Required. Must match exactly." },
          ],
        },
        {
          type: "text",
          text:
            "Errors appear under a field once you leave it, or as soon as you first press Save.",
        },
      ],
    },
    {
      id: "editing",
      label: "Editing a user",
      icon: "Pencil",
      blocks: [
        {
          type: "text",
          text:
            "Editing lets you change the display name and email. User ID is locked, and the password fields are not shown at all.",
        },
        {
          type: "note",
          tone: "info",
          text:
            "This screen cannot change someone else's password. A user changes their own from Edit profile, reached through the menu in the top-right corner.",
        },
      ],
    },
    {
      id: "deleting",
      label: "Deleting users",
      icon: "Alert",
      blocks: [
        {
          type: "text",
          text:
            "Both the row bin and the bulk bin ask for confirmation. Bulk delete covers every row you have ticked, including ones on other pages.",
        },
        {
          type: "note",
          tone: "warn",
          text:
            "Deleting an account is permanent, and nothing stops you deleting the account you are signed in with. Check the display name in the confirmation before agreeing.",
        },
      ],
    },
  ],
};

/* ================================================================== */
/*  Application Configuration                                          */
/* ================================================================== */

const appConfigHelp = {
  title: "Application Configuration",
  subtitle: "The runtime settings the server is using — test target, AI provider and integrations.",
  sections: [
    {
      id: "what",
      label: "What this screen is",
      icon: "Book",
      blocks: [
        {
          type: "text",
          text:
            "A window onto how the server is currently configured, grouped into six cards. Each card has a pencil icon that opens just that group for editing.",
        },
        {
          type: "note",
          tone: "info",
          text:
            "A value shown in grey italics as \"not set\" means the server has no value for it, not that it is blank.",
        },
      ],
    },
    {
      id: "sections",
      label: "What each card controls",
      icon: "List",
      blocks: [
        {
          type: "fields",
          items: [
            { term: "Application", desc: "The app's own name and which environment it thinks it is." },
            {
              term: "Test Target",
              desc:
                "App Start URL is the address tests open. PlantOps API Base is the API they exercise. These decide what actually gets tested.",
            },
            { term: "AI Provider", desc: "Which model service the AI features call, the model, and its base URL." },
            {
              term: "Browser",
              desc:
                "Headless decides whether the test browser is visible. Slow-Mo adds a delay between actions, which helps when watching a run.",
            },
            { term: "GitHub", desc: "The UI and API repositories and the default branch." },
            { term: "Jira", desc: "The Jira site address and the account email used to reach it." },
          ],
        },
      ],
    },
    {
      id: "editing",
      label: "Editing a section",
      icon: "Pencil",
      blocks: [
        {
          type: "steps",
          items: [
            "Click the pencil on the card you want to change.",
            "Edit the fields. Reset restores what was loaded; Cancel or Escape closes without saving.",
            "Save writes that section and the screen updates from the server's reply.",
          ],
        },
        {
          type: "note",
          tone: "warn",
          text:
            "Saving applies straight away, with no confirmation step. There is no validation here either — Headless and Slow-Mo accept any text, so a typo becomes the live value.",
        },
      ],
    },
    {
      id: "care",
      label: "Things to watch",
      icon: "Alert",
      blocks: [
        {
          type: "list",
          items: [
            "There is no Refresh. The configuration is read once when the screen opens, so a value someone else changed since then will not show until you reload.",
            "Because of that, saving overwrites whatever is on the server, even if it changed after you opened the screen.",
            "If loading fails the cards do not render at all and nothing can be edited until the server responds.",
            "Changing App Start URL, PlantOps API Base or the AI base URL re-points live behaviour — every run afterwards uses the new value.",
          ],
        },
      ],
    },
  ],
};

/* ================================================================== */
/*  Profile                                                            */
/* ================================================================== */

const profileHelp = {
  title: "Settings › Profile",
  subtitle: "Your own account details, password and Jira account. Open it from Settings in the menu under your name.",
  sections: [
    {
      id: "what",
      label: "What this screen is",
      icon: "Book",
      blocks: [
        {
          type: "text",
          text:
            "Your own account, and the only place you can change your own password. It affects nobody else.",
        },
        {
          type: "fields",
          items: [
            { term: "User ID", desc: "Shown for reference and cannot be changed. It is what you sign in with." },
            { term: "Display Name", desc: "Required. How your name appears throughout the app." },
            { term: "Email", desc: "Required, and must be a valid address." },
          ],
        },
      ],
    },
    {
      id: "password",
      label: "Changing your password",
      icon: "Check",
      blocks: [
        {
          type: "text",
          text:
            "Leave both password fields blank and your current password is kept. Fill them in and it changes when you save.",
        },
        {
          type: "fields",
          items: [
            { term: "New password", desc: "At least 8 characters." },
            { term: "Confirm new password", desc: "Must match exactly." },
          ],
        },
      ],
    },
    {
      id: "saving",
      label: "Saving",
      icon: "Play",
      blocks: [
        {
          type: "text",
          text:
            "Save changes stays disabled until you have actually changed something and every field is valid. A green banner confirms the save; the password boxes clear themselves afterwards.",
        },
        {
          type: "note",
          tone: "info",
          text:
            "Your new display name and email appear elsewhere in the app immediately — no need to sign out and back in.",
        },
      ],
    },
  ],
};

/* ================================================================== */
/*  Settings                                                           */
/* ================================================================== */
const settingsHelp = {
  title: "Settings › Configuration",
  subtitle: "Your own configuration: the values your runs can use.",
  sections: [
    {
      id: "variables",
      label: "Variables",
      icon: "Book",
      blocks: [
        {
          type: "text",
          text:
            "A configuration entry keeps a value under the name the tests use, such as {BASE_URL} or {ADMIN_USER}. A private entry and its value are yours alone; a public one, with its value, is everyone's. Every value is shown as typed, passwords included. When Create Artifact or Execute asks for a variable's value, the › button next to it lists everything here, whatever its name, so DEV_URL can fill {BASE_URL}; one entry fills one field.",
        },
        {
          type: "fields",
          items: [
            { term: "Name", desc: "Capital letters, digits and _, starting with a letter (e.g. BASE_URL), as the tests write it." },
            { term: "Value", desc: "The value you get: a private entry's, or a public entry's shared value (or your own, once you set one)." },
            { term: "Edit", desc: "Change the name, description and value. On a public variable anyone can, after confirming it changes for every user." },
          ],
        },
      ],
    },
    {
      id: "visibility",
      label: "Private or public",
      icon: "Users",
      blocks: [
        {
          type: "fields",
          items: [
            { term: "Private", desc: "Only you see it and its value." },
            {
              term: "Public",
              desc: "Everyone sees it and uses its value. Anyone can change its name, description and value, or delete it: each asks first, as it changes the variable for every user. A private variable of the same name wins for its owner.",
            },
            {
              term: "Creator",
              desc: "Only the creator makes an entry private. Making a public one private removes the other users' values, after you confirm. Anyone else switching it to Private gets their own private copy.",
            },
          ],
        },
      ],
    },
  ],
};

/* Registry keyed by screen id. Add a screen here, then drop a <HelpButton
   screen="…" /> into its header. */
const HELP_CONTENT = {
  dashboard: dashboardHelp,
  boards: boardsHelp,
  "board-detail": boardDetailHelp,
  tickets: ticketsHelp,
  executions: executionsHelp,
  datasource: datasourceHelp,
  reports: reportsHelp,
  scheduling: schedulingHelp,
  users: usersHelp,
  appconfig: appConfigHelp,
  profile: profileHelp,
  settings: settingsHelp,
};

export function getHelp(screen) {
  return HELP_CONTENT[screen] || null;
}

export default HELP_CONTENT;
